import { Icon } from "@blocks/components";
import { localized } from "@blocks/utils";
import { BlockControls } from "@wordpress/block-editor";
// @ts-ignore
import { ToolbarDropdownMenu, ToolbarGroup } from "@wordpress/components";
import { dispatch, useSelect } from "@wordpress/data";
import { __ } from "@wordpress/i18n";
import { useShortcut } from "@wordpress/keyboard-shortcuts";
import { isKeyboardEvent } from "@wordpress/keycodes";
import React, { Fragment, useEffect, useMemo } from "react";
import useLocalStorage from "./useLocalStorage";

const IGNORED_ATTRIBUTES_FOR_COPY_PASTE_STYLES = [
	"clientId",
	"className",
	"cssID",
	"text",
	"link",
	"icon",
	"hasModal",
	"modalOnly",
	"columns",
	"childRow",
	"markup",
	"blockCSS",
	"image",
	"alt1",
	"alt2",
	"colWidth",
	"blockHTML",
];

const useCopyPasterStyles = () => {
	const [value, setValue] = useLocalStorage<Record<string, any>>({
		key: "_magazine_blocks_styles",
		defaultValue: {},
	});

	useEffect(() => {
		const { registerShortcut, unregisterShortcut } = dispatch(
			// @ts-ignore
			"core/keyboard-shortcuts"
		) as any;

		registerShortcut({
			name: "magazine-blocks/copy-styles",
			category: "block",
			description: __("Copy block styles", "magazine-blocks"),
			keyCombination: {
				modifier: "ctrl",
				character: "c",
			},
		});

		registerShortcut({
			name: "magazine-blocks/paste-styles",
			category: "block",
			description: __("Paste block styles", "magazine-blocks"),
			keyCombination: {
				modifier: "primaryAlt",
				character: "v",
			},
		});

		return () => {
			unregisterShortcut("magazine-blocks/copy-styles");
			unregisterShortcut("magazine-blocks/paste-styles");
		};
	}, [setValue]);

	const { allShortcutKeys, clientId, getAttributes, blockName } = useSelect(
		(selector: any) => {
			const {
				getAllShortcutKeyCombinations,
				getAllShortcutRawKeyCombinations,
			} = selector("core/keyboard-shortcuts");
			const {
				getSelectedBlockClientId,
				getBlockAttributes,
				getBlockName,
			} = selector("core/block-editor");
			return {
				allShortcutKeys:
					getAllShortcutKeyCombinations ||
					getAllShortcutRawKeyCombinations,
				clientId: getSelectedBlockClientId(),
				getAttributes: getBlockAttributes,
				blockName:
					getSelectedBlockClientId() &&
					getBlockName(getSelectedBlockClientId()),
			};
		},
		[]
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const copyStyles = () => {
		const styles = {};
		const attributes = getAttributes(clientId) ?? {};

		for (const attribute of Object.keys(attributes)) {
			if (!IGNORED_ATTRIBUTES_FOR_COPY_PASTE_STYLES.includes(attribute)) {
				styles[attribute] = attributes[attribute];
			}
		}

		setValue((prev) => ({
			...prev,
			[blockName]: styles,
		}));
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const pasteStyles = () => {
		const { updateBlockAttributes } = dispatch("core/block-editor") as any;
		const styles = value?.[blockName];
		if (styles) {
			updateBlockAttributes(clientId, { ...styles });
		}
	};

	const isMatch = (name: string, event: keyof WindowEventMap) => {
		return allShortcutKeys(name).some(
			(
				_ref:
					| string
					| {
							modifier?: string;
							character?: string;
					  }
			) => {
				let modifier, character;

				if ("object" === typeof _ref) {
					modifier = _ref?.modifier;
					character = _ref?.character;
				} else if ("string" === typeof _ref) {
					if ("ctrl+c" === _ref) {
						modifier = "ctrl";
						character = "c";
					} else if ("ctrl+alt+v" === _ref) {
						modifier = "primaryAlt";
						character = "v";
					}
				}

				if (modifier && character) {
					return isKeyboardEvent[modifier](event, character);
				}

				return false;
			}
		);
	};

	useShortcut(
		"magazine-blocks/copy-styles",
		(event: keyof WindowEventMap) => {
			if (
				blockName?.includes("mzb") &&
				isMatch("magazine-blocks/copy-styles", event)
			) {
				copyStyles();
			}
		},
		{ bindGlobal: true, eventName: "keydown" }
	);

	useShortcut(
		"magazine-blocks/paste-styles",
		(event: keyof WindowEventMap) => {
			if (
				blockName?.includes("mzb") &&
				isMatch("magazine-blocks/paste-styles", event)
			) {
				pasteStyles();
			}
		},
		{ bindGlobal: true, eventName: "keydown" }
	);

	const CopyPasterStyleBlockControl: React.FC<{
		withBlockControls?: boolean;
	}> = useMemo(() => {
		const Control = (props: { withBlockControls?: boolean }) => {
			if (!localized.configs.editor["copy-paste-styles"]) return null;
			const Wrapper = props.withBlockControls
				? (props: { children: React.ReactNode }) => {
						return (
							<BlockControls>
								<ToolbarGroup>{props.children}</ToolbarGroup>
							</BlockControls>
						);
				  }
				: Fragment;
			return (
				<Wrapper>
					<ToolbarDropdownMenu
						icon={<Icon type="controlIcon" name="copy" />}
						label={__("Copy/Paste Styles", "magazine-blocks")}
						controls={[
							{
								icon: <Icon type="controlIcon" name="copy" />,
								title: __(
									"Copy Styles Ctrl+C",
									"magazine-blocks"
								),
								onClick: copyStyles,
							},
							{
								icon: <Icon type="controlIcon" name="paste" />,
								title: __(
									"Paste Styles Ctrl+Alt+V",
									"magazine-blocks"
								),
								onClick: pasteStyles,
								isDisabled: !value?.[blockName],
							},
						]}
					/>
				</Wrapper>
			);
		};
		return Control;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blockName, copyStyles, pasteStyles, value]);

	return {
		CopyPasterStyleBlockControl,
		copyStyles,
		pasteStyles,
	};
};

export default useCopyPasterStyles;
